import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DragDropContext, Droppable } from "react-beautiful-dnd";

import { makeStyles } from "@material-ui/core/styles";
import { Paper, Typography, Checkbox, FormControlLabel } from "@material-ui/core";

import HorizontalAssetPreview from "../../AssetUploader/Shared/HorizontalAssetPreview";
import {
  groupIdsByDimension,
  getDurationOptions
} from "../../AssetUploader/Shared/dimensionUtils";
import {
  reorderCreatives,
  selectCreatives,
  deselectCreatives
} from "../../../actions/app/creatives";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    // border: "1px solid rgba(0, 0, 0, 0.26)",
    boxShadow: "none",
    display: "flex",
    justifyContent: "space-around"
  },
  aside: {
    width: "95%"
  },
  h6: {
    fontWeight: 400,
    fontSize: "1.15rem"
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    textAlign: "left",
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.5)
  },
  groupTitle: {
    fontWeight: 500,
    color: theme.palette.text.primary
  },
  groupCount: {
    fontWeight: 400,
    color: theme.palette.text.secondary
  },
  groupToggleLabel: {
    fontSize: "0.8125rem"
  }
}));

export default function HorizontalAssetList({
  isClickEnabled,
  isDragDisabled,
  handleSelectCard = () => {},
  isWeightEditable,
  isThumbnailEnabled,
  isWeightEnabled,
  isDuplicateEnabled,
  uploadLimit,
  selectedIds,
  filterFn,
  groupByDimension,
  // Review renders one list per ad, so each needs its own droppable. They sit
  // in separate DragDropContexts, but keeping the ids distinct avoids relying
  // on that for correctness.
  droppableId = "droppable-1"
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const creatives = useSelector((state) => state.creatives);
  const selectedList = selectedIds ? selectedIds : creatives.selectedIds;

  // Video and audio group by duration rather than a 0x0 size, snapped to the
  // configured Dimensions list. Memoized because getDurationOptions builds a
  // new array each call, which would defeat referential equality downstream.
  const typeGroups = useSelector((state) => state.typeGroups);
  const durationOptions = useMemo(() => getDurationOptions(typeGroups), [
    typeGroups
  ]);

  // react-beautiful-dnd requires every Draggable in a Droppable to carry a
  // contiguous 0..n-1 index. Resolve the rendered subset up front so both the
  // Draggable indices and the drag result are expressed against the same
  // list — previously the index came from allIds, so each skipped backup /
  // companion / filtered-out creative left a gap and drags landed wrong.
  const visibleIds = creatives.allIds.filter((id) => {
    const creative = creatives.byId[id];
    if (!creative) return false;
    if (creative.creativeRole !== "PRIMARY") return false;
    if (filterFn && !filterFn(creative)) return false;
    return true;
  });

  // Grouping rearranges the rendered order, so the indices handed to each
  // Draggable — and the list onDragEnd splices against — have to follow the
  // grouped order, not allIds order. Both fall out of the same orderedIds.
  const dimensionGroups = groupByDimension
    ? groupIdsByDimension(visibleIds, creatives.byId, durationOptions)
    : [];

  const orderedIds = groupByDimension
    ? dimensionGroups.reduce((ids, group) => ids.concat(group.ids), [])
    : visibleIds;

  // Draggable indices run across the whole droppable, not per group, so each
  // group's cards start where the previous group's ended.
  const groupStartIndex = new Map();
  dimensionGroups.reduce((start, group) => {
    groupStartIndex.set(group.key, start);
    return start + group.ids.length;
  }, 0);

  // The group toggle drives the redux selection. A list whose selection is
  // controlled by a parent (backup / companion steps pass their own
  // selectedIds) doesn't read that, so don't offer a control that would look
  // like it did nothing.
  const isGroupSelectEnabled =
    groupByDimension && isClickEnabled && !selectedIds;

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return; // dropped outside the list
    if (destination.index === source.index) return;

    const nextVisibleIds = [...orderedIds];
    nextVisibleIds.splice(source.index, 1);
    nextVisibleIds.splice(destination.index, 0, draggableId);

    dispatch(reorderCreatives(nextVisibleIds));
  };

  const handleToggleGroup = (group) => {
    const isWholeGroupSelected = group.ids.every((id) =>
      selectedList.includes(id)
    );

    if (isWholeGroupSelected) {
      dispatch(deselectCreatives(group.ids));
    } else {
      dispatch(selectCreatives(group.ids));
    }
  };

  const renderCard = (id, index) => (
    <HorizontalAssetPreview
      id={id}
      index={index}
      key={id}
      asset={creatives.byId[id]}
      isClickEnabled={isClickEnabled}
      isDragDisabled={isDragDisabled}
      isCardSelected={selectedList.includes(id)}
      handleSelectedCard={handleSelectCard}
      isWeightEditable={isWeightEditable}
      isThumbnailEnabled={isThumbnailEnabled}
      isWeightEnabled={isWeightEnabled}
      isDuplicateEnabled={isDuplicateEnabled}
      uploadLimit={uploadLimit}
    />
  );

  const renderGroup = (group) => {
    const selectedCount = group.ids.filter((id) => selectedList.includes(id))
      .length;
    const isWholeGroupSelected = selectedCount === group.ids.length;

    return (
      <div key={group.key}>
        <div className={classes.groupHeader}>
          <Typography variant="subtitle2" className={classes.groupTitle}>
            {group.label}{" "}
            <span className={classes.groupCount}>
              {`· ${group.ids.length} creative${
                group.ids.length === 1 ? "" : "s"
              }`}
            </span>
          </Typography>
          {isGroupSelectEnabled ? (
            <FormControlLabel
              classes={{ label: classes.groupToggleLabel }}
              control={
                <Checkbox
                  color="primary"
                  size="small"
                  checked={isWholeGroupSelected}
                  indeterminate={selectedCount > 0 && !isWholeGroupSelected}
                  onChange={() => handleToggleGroup(group)}
                  inputProps={{
                    "aria-label": `Select all ${group.label} creatives`
                  }}
                />
              }
              label="Select all"
            />
          ) : null}
        </div>
        {group.ids.map((id, i) =>
          renderCard(id, groupStartIndex.get(group.key) + i)
        )}
      </div>
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={droppableId}>
        {(provided) => (
          <Paper
            innerRef={provided.innerRef}
            {...provided.droppableProps}
            className={classes.paper}
          >
            <aside className={classes.aside}>
              {groupByDimension
                ? dimensionGroups.map(renderGroup)
                : orderedIds.map((id, index) => renderCard(id, index))}
              {provided.placeholder}
            </aside>
          </Paper>
        )}
      </Droppable>
    </DragDropContext>
  );
}
